import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { FiShoppingCart, FiSearch, FiChevronLeft, FiChevronRight, FiGrid, FiBox, FiPlus, FiList } from 'react-icons/fi';
import { FaDiscord, FaCoins, FaDollarSign } from 'react-icons/fa';
import { GiBroadsword, GiShieldcomb, GiHelmet, GiBoots, GiEarrings, GiNecklace } from 'react-icons/gi';
import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import logo from '../../../../assets/images/logo.png';
import { backendUrl } from '../../../../utilities/axios';
import { resolveAvatar } from '../../../../utilities/avatar';
import { getShopItems } from '../../../../store/shopItems/shopItemsSlice';
import {
  PageWrapper, PageBanner, ControlsBar, FilterBtn,
  ItemsGrid, ItemCard, PaginationBar, PageBtn,
  RequestBtn,
} from './ShopStyle';
import { SkeletonCard, NoData } from '../../../shared/WebsiteFeedback';
import BuyModal from './BuyModal';
import WantedOfferModal from './WantedOfferModal';

const DISCORD_INVITE_URL = 'https://discord.gg/NMBTSzDPy7';

const PER_PAGE = 12;

const CATEGORY_KEYS = ['all', 'weapon', 'armor', 'helmet', 'boots', 'earring', 'necklace'];
const CATEGORY_DB_MAP = {
  weapon: 'سلاح',
  armor: 'درع',
  helmet: 'خوذه',
  boots: 'حذاء',
  earring: 'حلق',
  necklace: 'قلادة',
};
const CATEGORY_ICONS = {
  all: FiGrid,
  weapon: GiBroadsword,
  armor: GiShieldcomb,
  helmet: GiHelmet,
  boots: GiBoots,
  earring: GiEarrings,
  necklace: GiNecklace,
};
const categoryKeyFromDbValue = (dbVal) =>
  Object.keys(CATEGORY_DB_MAP).find(key => CATEGORY_DB_MAP[key] === dbVal) || 'weapon';

const SORT_KEYS = ['newest', 'price_high', 'price_low', 'sell_only', 'buy_only'];

const Shop = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: shopData, isLoading } = useSelector(state => state.shopItems);
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settings);
  const discordInvite = settings.data?.discord || DISCORD_INVITE_URL;
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('available');
  const [buyItem, setBuyItem] = useState(null);
  const [offerItem, setOfferItem] = useState(null);

  useEffect(() => {
    dispatch(getShopItems({
      page: 1,
      size: 100,
      query: '',
      filter: JSON.stringify({ status: 'approved', item_status: tab === 'available' ? 'available' : 'sold' }),
    }));
  }, [dispatch, tab]);

  const allItems = shopData?.data || [];

  const getCategoryLabel = (key) => {
    const map = {
      all: t('website.shop.cat_all'),
      weapon: t('website.shop.cat_weapon'),
      armor: t('website.shop.cat_armor'),
      helmet: t('website.shop.cat_helmet'),
      boots: t('website.shop.cat_boots'),
      earring: t('website.shop.cat_earring'),
      necklace: t('website.shop.cat_necklace'),
    };
    return map[key] || key;
  };

  const getSortLabel = (key) => {
    const map = {
      newest: t('website.shop.sort_newest'),
      price_high: t('website.shop.sort_price_high'),
      price_low: t('website.shop.sort_price_low'),
      sell_only: t('website.shop.sort_sell_only'),
      buy_only: t('website.shop.sort_buy_only'),
    };
    return map[key] || key;
  };

  const processed = useMemo(() => {
    let list = allItems;
    if (category !== 'all') {
      const dbVal = CATEGORY_DB_MAP[category];
      list = list.filter(i => i.category === dbVal);
    }
    if (search.trim()) list = list.filter(i =>
      i.name.includes(search.trim()) || i.seller?.includes(search.trim()) || i.server?.includes(search.trim())
    );
    if (sort === 'sell_only') return list.filter(i => i.type === 'بيع');
    if (sort === 'buy_only') return list.filter(i => i.type === 'شراء');
    return [...list].sort((a, b) => {
      if (sort === 'price_high') return b.price_num - a.price_num;
      if (sort === 'price_low') return a.price_num - b.price_num;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [search, category, sort, allItems]);

  const totalPages = Math.max(1, Math.ceil(processed.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = processed.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const goTo = p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const typeColor = () => '#ef4444';
  const typeLabel = (type) => type === 'بيع' ? t('website.shop.type_sell') : t('website.shop.type_buy');

  const handleRequestClick = (item) => {
    if (!auth.isAuth) { navigate('/login'); return; }
    if (item.type === 'بيع') setBuyItem(item);
    else setOfferItem(item);
  };

  const refreshShopItems = () => {
    dispatch(getShopItems({
      page: 1,
      size: 100,
      query: '',
      filter: JSON.stringify({ status: 'approved', item_status: tab === 'available' ? 'available' : 'sold' }),
    }));
  };

  return (
    <WebsiteLayout>
      <PageWrapper>
        <PageBanner>
          <div>
            <div className="page-title"><FiShoppingCart /> {t('website.shop.title')}</div>
            <div className="page-sub">{t('website.shop.subtitle')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {auth.isAuth && (
              <button className="post-btn" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)' }}
                onClick={() => navigate('/my-shop-items')}>
                <FiList /> {t('website.shop.my_ads_btn')}
              </button>
            )}
            <button className="post-btn" onClick={() => navigate(auth.isAuth ? '/add-shop-item' : '/login')}>
              <FiPlus /> {t('website.shop.post_ad_btn')}
            </button>
          </div>
        </PageBanner>

        <ControlsBar style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <div className="filter-pills" style={{ flex: 'unset' }}>
            <FilterBtn active={tab === 'available'} onClick={() => { setTab('available'); setPage(1); }}>
              {t('website.shop.tab_available')}
            </FilterBtn>
            <FilterBtn active={tab === 'unavailable'} onClick={() => { setTab('unavailable'); setPage(1); }}>
              {t('website.shop.tab_unavailable')}
            </FilterBtn>
          </div>
        </ControlsBar>

        <ControlsBar>
          <div className="search-wrap">
            <FiSearch />
            <input placeholder={t('website.shop.search_placeholder')} value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <div className="filter-pills">
            {CATEGORY_KEYS.map(key => {
              const Icon = CATEGORY_ICONS[key];
              return (
                <FilterBtn key={key} active={category === key} onClick={() => { setCategory(key); setPage(1); }}>
                  <Icon /> {getCategoryLabel(key)}
                </FilterBtn>
              );
            })}
          </div>
          <select className="sort-select" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
            {SORT_KEYS.map(key => <option key={key} value={key}>{getSortLabel(key)}</option>)}
          </select>
        </ControlsBar>

        {isLoading ? (
          <ItemsGrid>
            {Array.from({ length: 12 }, (_, i) => <SkeletonCard key={i} />)}
          </ItemsGrid>
        ) : pageItems.length === 0 ? (
          <NoData title={tab === 'unavailable' ? t('website.shop.unavailable_empty') : undefined} />
        ) : (
          <ItemsGrid>
            {pageItems.map(item => {
              const tc = typeColor();
              const CatIcon = CATEGORY_ICONS[categoryKeyFromDbValue(item.category)] || FiBox;
              const isBuyRequest = item.type === 'شراء';
              return (
                <ItemCard key={item._id}>
                  <div className="item-img-wrap">
                    {item.images?.[0] ? (
                      <img className="item-img" src={`${backendUrl}/shopItems/${item.images[0]}`} alt={item.name} />
                    ) : (
                      <CatIcon className="item-icon-fallback" />
                    )}
                    <span className="type-badge"
                      style={{ background: tc + '22', color: tc, border: `1px solid ${tc}44` }}>
                      {typeLabel(item.type)}
                    </span>
                    <span className="category-badge"><CatIcon /> {item.category}</span>
                  </div>
                  <div className="item-body">
                    <div className="item-name">{item.name}</div>
                    <div className="item-server">{item.server}</div>
                    <div className="item-price"><FaDollarSign /> {item.price_num}</div>
                    <div className="seller-row">
                      <img src={resolveAvatar(item.owner) || logo} alt={item.owner?.name || item.seller} />
                      <span className="seller-name">{item.owner?.name || item.seller}</span>
                    </div>
                    {tab === 'unavailable' ? (
                      <RequestBtn disabled style={{ background: '#3f3f3f', cursor: 'not-allowed' }}>
                        {t('website.shop.sold_reason')}
                      </RequestBtn>
                    ) : (
                      <RequestBtn onClick={e => { e.stopPropagation(); handleRequestClick(item); }}>
                        <FaDiscord /> {isBuyRequest ? t('website.shop.have_item_btn') : t('website.shop.request_btn')}
                      </RequestBtn>
                    )}
                  </div>
                </ItemCard>
              );
            })}
          </ItemsGrid>
        )}

        {!isLoading && totalPages > 1 && (
          <PaginationBar>
            <div className="page-info">
              {t('website.shop.showing')} <span>{(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, processed.length)}</span> {t('website.shop.of')} <span>{processed.length}</span> {t('website.shop.ad_unit')}
            </div>
            <div className="pages">
              <PageBtn disabled={currentPage === 1} onClick={() => goTo(currentPage - 1)}><FiChevronRight /></PageBtn>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <PageBtn key={n} active={n === currentPage} onClick={() => goTo(n)}>{n}</PageBtn>
              ))}
              <PageBtn disabled={currentPage === totalPages} onClick={() => goTo(currentPage + 1)}><FiChevronLeft /></PageBtn>
            </div>
          </PaginationBar>
        )}
      </PageWrapper>

      {buyItem && (
        <BuyModal
          item={buyItem}
          discordInvite={discordInvite}
          onClose={() => setBuyItem(null)}
          onPurchased={refreshShopItems}
        />
      )}
      {offerItem && (
        <WantedOfferModal
          item={offerItem}
          discordInvite={discordInvite}
          onClose={() => setOfferItem(null)}
        />
      )}
    </WebsiteLayout>
  );
};

export default Shop;
